import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { type ProgramSasApiGateway } from "@modules/programs/programSas/gateway/programSasApiGateway"
import { type PendingSubscribersTotalDto } from "@modules/programs/programSas/gateway/programSasDto"
import {
  type ProgramSubscriberDto,
  ProgramSubscriberDtoSchema,
} from "@modules/programs/programSubscriber/gateway/programSubscriberDto"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { type Page, type PaginatedWrapper, wrapperToPage } from "@modules/utils/paginated"
import dayjs from "dayjs"

export class ProgramSasApi implements ProgramSasApiGateway {
  private baseUrl = "/programs"
  private buildSASRequestUrl = (programId: string) => `${this.baseUrl}/${programId}/requests`

  constructor(private api: ApiClient) {}

  async getProgramSasPendingSubscriptions(
    programId: string,
    status: ProgramSubscriptionStatus,
    page?: number,
    size?: number,
    name?: string,
    startDate?: Date,
  ): Promise<Page<ProgramSubscriberDto>> {
    const result = await this.api.get<PaginatedWrapper<ProgramSubscriberDto>>(
      `${this.buildSASRequestUrl(programId)}/subscriptions`,
      {
        params: {
          status,
          startDate: startDate ? dayjs(startDate).toISOString() : undefined,
          name,
          page,
          size,
        },
      },
    )
    const pageResult = wrapperToPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content.map((programSubscriberDto) =>
        LoggerService.parseApiDto(ProgramSubscriberDtoSchema, programSubscriberDto),
      ),
    }
  }

  async getProgramSasTotalPendingSubscriptions(programId: string, startDate?: Date) {
    const result = await this.api.get<PendingSubscribersTotalDto>(
      `${this.buildSASRequestUrl(programId)}/total-pending`,
      {
        params: {
          startDate: startDate ? dayjs(startDate).toISOString() : undefined,
        },
      },
    )

    return result.data
  }

  async acceptAllProgramSasSubscriptions(programId: string) {
    await this.api.post<{ status: string }>(`${this.buildSASRequestUrl(programId)}/accept-all-subscriptions`, {})
  }

  async acceptProgramSasSubscriptions(programId: string, subscriptionIds: string[]) {
    await this.api.post<{ status: string }>(`${this.buildSASRequestUrl(programId)}/accept-subscriptions`, {
      subscriptionIds,
    })
  }

  async acceptProgramSasSubscription(programId: string, subscriptionId: string) {
    await this.api.post<string>(`${this.buildSASRequestUrl(programId)}/${subscriptionId}/accept-subscription`, {
      subscriptionId,
    })
  }

  async rejectProgramSasSubscription(programId: string, subscriptionId: string) {
    await this.api.post<string>(`${this.buildSASRequestUrl(programId)}/${subscriptionId}/reject-subscription`, {
      subscriptionId,
    })
  }
}
